import { Reveal, Eyebrow, PageHero, PrimaryLink } from "@/components/shared";
import { Flower2, BookOpen, Library, Landmark } from "lucide-react";
import { IMAGES } from "@/data/content";

const pillars = [
  {
    icon: Flower2,
    title: "Meditation",
    body: "Regular meditation sittings, retreats and Dhamma teachings open to all — cultivating calm, clarity and compassion in daily life. Sessions are led by monastics and welcome beginners and experienced practitioners alike.",
  },
  {
    icon: BookOpen,
    title: "Nalanda Study Centre",
    body: "A living centre for Dhamma study, scholarship and practice, reviving the spirit of ancient Nalanda in Karnataka. The centre hosts courses, seminars and monastic training for a new generation of students.",
  },
  {
    icon: Library,
    title: "Library",
    body: "A growing collection of Buddhist texts, Pali literature, scholarly works and regional translations — open to students, researchers and anyone seeking to understand the Buddha’s teaching at its source.",
  },
  {
    icon: Landmark,
    title: "Buddhist Heritage",
    body: "From the Sacred Buddha Relic Initiative to advocacy for the Ashokan Rock Edicts, IBMCT works to preserve, honour and share India’s profound Buddhist heritage with future generations.",
  },
];

const Dhamma = () => (
  <>
    <PageHero
      testId="dhamma-hero"
      eyebrow="Dhamma"
      title="The Teaching at the Heart of Our Work"
      intro="Every IBMCT initiative flows from the Dhamma — the Buddha’s path of wisdom, ethical conduct and compassion. These are the ways we keep that teaching alive."
    />

    <section className="bg-ivory" data-testid="dhamma-pillars-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 80}>
              <article className="card-ivory p-8 h-full" data-testid={`dhamma-pillar-${i}`}>
                <p.icon size={30} strokeWidth={1.5} className="text-saffron" aria-hidden="true" />
                <h2 className="mt-5 font-serif text-2xl font-semibold text-charcoal">{p.title}</h2>
                <p className="mt-3 text-charcoal/80 leading-relaxed text-[15.5px]">{p.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-coal text-white" data-testid="dhamma-quote-section">
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20 text-center">
        <Reveal>
          <div className="w-16 h-px bg-saffron mx-auto mb-10" aria-hidden="true" />
          <blockquote className="font-serif text-2xl sm:text-3xl leading-snug">
            “Thousands of candles can be lit from a single candle, and the life of the candle will not be shortened. Happiness never
            decreases by being shared.”
          </blockquote>
          <p className="mt-8 font-sans text-sm tracking-[0.18em] uppercase text-saffron-light">— The Buddha</p>
          <div className="mt-10">
            <PrimaryLink to="/nalanda" testId="dhamma-explore-nalanda-button">Explore the Nalanda Study Centre</PrimaryLink>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-ivory" data-testid="dhamma-image-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <img src={IMAGES.svcDhamma} alt="Dhamma teaching and practice at IBMCT" className="rounded-lg w-full object-cover object-center aspect-[4/3]" data-testid="dhamma-image" />
        </Reveal>
        <Reveal delay={100}>
          <Eyebrow>Practice With Us</Eyebrow>
          <h2 className="text-[32px] sm:text-4xl font-semibold text-charcoal">All Are Welcome</h2>
          <p className="mt-5 text-charcoal/80 leading-relaxed">
            Whether you are taking your first step onto the path or deepening a lifelong practice, IBMCT’s doors are open. Join a
            meditation sitting, attend a Dhamma talk, or spend time in study at the Nalanda centre.
          </p>
          <div className="mt-8">
            <PrimaryLink to="/contact" testId="dhamma-contact-button">Get in Touch</PrimaryLink>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Dhamma;
